import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Image,
  Animated,
  Easing,
  useWindowDimensions,
  StyleSheet,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppImages } from "../utility/AppImages";
import { AppColors } from "../utility/AppColors";
import { AppFontStyle } from "../styles/AppFontStyle";

const CustomCoinToast = ({
  isVisible,
  onClose,
  customCoinToastCoin,
  customCoinToastText,
  customCoinToastDay,
}: {
  isVisible: boolean;
  onClose: () => void;
  customCoinToastCoin: number;
  customCoinToastText: string;
  customCoinToastDay: number;
}) => {
  const deviceSize = useDeviceSize();
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  const styles = useStyles();
  const fadeAnim = useRef(new Animated.Value(0)).current;


  // Prevent double‐running in StrictMode/development
  const hasAnimatedIn = useRef(false);

  useEffect(() => {
    if (isVisible) {
      if (!hasAnimatedIn.current) {
        hasAnimatedIn.current = true;
        fadeAnim.setValue(0);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 500,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }).start();

        const timer = setTimeout(() => {
          onClose();
        }, 5000);

        return () => clearTimeout(timer);
      }
      return;
    }
    hasAnimatedIn.current = false;
  }, [isVisible, fadeAnim, onClose]);

  if (!isVisible) return null;

  return (
    <Modal transparent visible={isVisible} onRequestClose={onClose}>
      {/* 
        The outer View has pointerEvents="box-none" so that
        touches can reach its children appropriately.
      */}
      <View style={styles.modalOverlay} pointerEvents="box-none">
        {/* 
          BACKGROUND OVERLAY: catches taps anywhere outside the toast.
          This View sits behind the toast content.
        */}
        <TouchableOpacity
          style={styles.overlayBackground}
          activeOpacity={1}
          onPress={onClose}
        />

        {/* 
          TOAST CONTAINER: sits on top of the overlayBackground.
          pointerEvents="box-none" allows child TouchableOpacity
          to intercept taps (i.e. the close button).
        */}
        <Animated.View
          pointerEvents="box-none"
          style={[
            styles.modalContent,
            {
              top: 60,
              opacity: fadeAnim,
              transform: [
                {
                  translateY: fadeAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [20, 0],
                  }),
                },
              ],
              ...(isMobile ? { left: 10, right: 10 } : { right: 10 }),
              width: isMobile ? screenWidth - 20 : 500,
            },
          ]}
        >
          {/* 
            CLOSE BUTTON: an absolute 32×32 touchable area inside the toast.
            pointerEvents="auto" ensures it receives taps before the toast
            background. 
          */}
          <TouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          // pointerEvents="auto"
          >
            <Image
              source={AppImages.icCloseWhiteSvg}
              style={styles.closeIcon}
            />
          </TouchableOpacity>

          {/* CONTENT ROW */}
          <View style={styles.innerRow}>
            <View>
              <View style={styles.coinContainer}>
                <Image
                  source={AppImages.icCoinBelt}
                  style={styles.coinImage}
                  resizeMode="contain"
                />
                <Text
                  style={[
                    AppFontStyle.MEDIUM_ANYBODY_14,
                    { color: AppColors.white, textAlign: "center" },
                  ]}
                >
                  {customCoinToastDay ? `Day ${customCoinToastDay}` : " "}
                </Text>
              </View>
            </View>

            <View style={styles.textContainer}>
              <Text
                style={[
                  isMobile
                    ? AppFontStyle.REGULAR_ANYBODY_16
                    : AppFontStyle.REGULAR_ANYBODY_22,
                ]}
              >
                {customCoinToastCoin} coins
              </Text>
              <Text
                style={[
                  isMobile
                    ? AppFontStyle.MEDIUM_ANYBODY_14
                    : AppFontStyle.MEDIUM_ANYBODY_16,
                  { color: AppColors.white },
                ]}
              >
                {customCoinToastText}
              </Text>
            </View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    modalOverlay: {
      flex: 1,
    },
    overlayBackground: {
      position: "absolute",
      width: "100%",
      height: "100%",
      backgroundColor: "transparent",
    },
    modalContent: {
      position: "absolute",
      backgroundColor: AppColors.purple_453B50,
      padding: 8,
      borderRadius: 8,
      elevation: 5,
      borderWidth: 1,
      borderColor: AppColors.gray_A6A3AA,
    },
    closeButton: {
      position: "absolute",
      top: 8,
      right: 8,
      width: 32,
      height: 32,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10,
      cursor: "pointer"
    },
    closeIcon: {
      width: 16,
      height: 16,
    },
    innerRow: {
      flexDirection: "row",
      gap: 8,
      paddingTop: 32, // leave space for close button
    },
    coinContainer: {
      backgroundColor: AppColors.appViolentColor_6D08C3,
      borderRadius: 8,
      padding: 6,
      gap: 5,
      width: 80,
      alignItems: "center",
    },
    coinImage: {
      height: 48,
      width: 54,
    },
    textContainer: {
      flex: 1,
      justifyContent: "space-between",
      paddingRight: 30, // space for close button
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {
      coinContainer: {
        width: 70,
        padding: 4,
      },
      coinImage: {
        height: 40,
        width: 45,
      },
      innerRow: {
        paddingTop: 25,
      },
    },
    [DEVICE_SIZES.XS]: {
      coinContainer: {
        width: 70,
        padding: 4,
      },
      coinImage: {
        height: 40,
        width: 45,
      },
      innerRow: {
        paddingTop: 25,
      },
    },
  }
);

export default CustomCoinToast;
